import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import Header from "../components/Header";
import { subscriberApi } from "../lib/api";

export default function SubscriberResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";

  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd !== confirm) {
      setError("La confirmation ne correspond pas.");
      return;
    }
    if (pwd.length < 8) {
      setError("Mot de passe trop court (min 8 caractères).");
      return;
    }
    setSubmitting(true);
    try {
      await subscriberApi.resetPassword(token, pwd);
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (err) {
      setError(err?.response?.data?.detail || "Lien invalide ou expiré.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="subscriber-reset-page">
      <Header />
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white border border-primary/15 rounded-sm shadow-lg p-8">
          <h1 className="font-display text-2xl font-semibold text-primary mb-1">
            Nouveau mot de passe
          </h1>
          <p className="text-sm text-foreground/60 mb-6">
            Choisissez un nouveau mot de passe pour votre compte abonné (min 8 caractères).
          </p>

          {!token && (
            <div className="text-xs bg-[#FEE2E2] border border-[#FECACA] text-[#991B1B] px-3 py-2 rounded-sm mb-4">
              <AlertTriangle size={12} className="inline mr-1 -mt-0.5" />
              Lien invalide — aucun token fourni.
            </div>
          )}

          {done ? (
            <div className="text-center" data-testid="sub-reset-success">
              <div className="w-14 h-14 mx-auto bg-[#D1FAE5] text-[#065F46] rounded-sm flex items-center justify-center mb-4">
                <CheckCircle2 size={26} />
              </div>
              <h2 className="font-display text-xl font-semibold text-primary mb-1">
                Mot de passe réinitialisé
              </h2>
              <p className="text-sm text-foreground/70">Redirection vers la connexion…</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <label className="block">
                <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/60 mb-1">
                  Nouveau mot de passe
                </span>
                <div className="border border-primary/20 rounded-sm px-3 focus-within:border-primary transition-colors bg-white">
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    className="w-full bg-transparent text-sm py-2.5 outline-none font-mono"
                    data-testid="sub-reset-password"
                  />
                </div>
              </label>

              <label className="block">
                <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/60 mb-1">
                  Confirmation
                </span>
                <div className="border border-primary/20 rounded-sm px-3 focus-within:border-primary transition-colors bg-white">
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="w-full bg-transparent text-sm py-2.5 outline-none font-mono"
                    data-testid="sub-reset-confirm"
                  />
                </div>
              </label>

              {error && (
                <div className="text-xs text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] px-3 py-2 rounded-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || !token}
                className="w-full inline-flex items-center justify-center gap-2 bg-primary text-white px-5 py-3 rounded-sm text-sm font-medium hover:bg-primary-light transition-colors disabled:opacity-60"
                data-testid="sub-reset-submit"
              >
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
                Réinitialiser
              </button>

              <Link
                to="/login"
                className="block text-center text-xs text-foreground/60 hover:text-primary mt-2"
              >
                ← Retour à la connexion
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
